'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAsyncListOptions<T> {
  initial: T[];
  total: number;
  fetcher: (offset: number) => Promise<T[]>;
}

interface UseAsyncListResult<T> {
  items: T[];
  loading: boolean;
  hasMore: boolean;
  /** Resolves once the next page has been appended (or rejected silently). */
  loadMore: () => Promise<void>;
}

/**
 * Async list with paged "load more" semantics. Each call to `loadMore()`
 * runs the supplied fetcher with the current offset and appends the
 * returned slice. Models the Phase 2 contract where Show-More buttons
 * re-run the same query against `/api/search/<entity>/page` — Phase 1
 * uses simulated pagers but the consumer code is identical.
 */
export function useAsyncList<T>({
  initial,
  total,
  fetcher,
}: UseAsyncListOptions<T>): UseAsyncListResult<T> {
  const [items, setItems] = useState<T[]>(initial);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadMore = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const next = await fetcher(items.length);
      if (!mounted.current) return;
      setItems((prev) => [...prev, ...next]);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [items.length, fetcher, loading]);

  const hasMore = items.length < total;
  return { items, loading, hasMore, loadMore };
}
